import { LockOpenOutlined, LockOutlined } from "@material-ui/icons";
import {
  APITenderOutcomeBulkUpdateInput,
  APITenderOutcomeSearchParams,
} from "api/tenderOutcomes";
import { observer } from "mobx-react-lite";
import React, { useCallback } from "react";
import { Form } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { useAsyncFn, useEffectOnce } from "react-use";
import styled from "styled-components";
import api from "../../api";
import Button from "../../components/Button";
import Loading from "../../components/Loading";
import { useStore } from "../../store";
import { Toast } from "../../utils";
import { confirmProjectLock, confirmProjectUnlock } from "../../utils/confirm";
import { useProject, useSave } from "../Workspace/hooks";
import TenderOutcomeTable from "./TenderOutcomeTable";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: 0;
  overflow: auto;
  padding: 1.25rem;
`;

const StyledDiv = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledButton = styled(Button)`
  &.btn {
    min-width: 22rem;
    font-size: 0.75rem;
    line-height: 1.25rem;
  }
`;

const Header = styled.h1`
  font-weight: bold;
  font-size: 1rem;
  line-height: 1rem;
`;

type Props = {};

type Inputs = APITenderOutcomeBulkUpdateInput;

const TenderOutcome: React.FC<Props> = () => {
  const store = useStore();
  const methods = useForm<Inputs>();
  const project = useProject();
  const { current: currentUser } = store.auth;
  const hasEditPermissions = !!(
    currentUser &&
    (currentUser.isAdmin || project?.adminUsers.includes(currentUser.id))
  );
  const editable = hasEditPermissions && !project?.isLocked;

  const [{ loading }, initFn] = useAsyncFn(async () => {
    if (project) {
      if (project.isLocked && project.snapshot?.tenderOutcome) {
        store.tenderOutcomes.saveListItem(project.snapshot.tenderOutcome as any);
        return Array.from(store.tenderOutcomes.listItems.values());
      }
      const params: APITenderOutcomeSearchParams = {
        project: project?.id,
      };
      await store.tenderOutcomes.list(undefined, undefined, params);
    }
    return Array.from(store.tenderOutcomes.listItems.values());
  }, [project?.id, !!project?.snapshot]);

  useEffectOnce(() => {
    initFn();
  });

  const handleClick = useCallback(async () => {
    if (!project) {
      return;
    }

    const { id, latestIssueDate, versionNumber } = project;
    if (!project.isLocked) {
      if (await confirmProjectLock(latestIssueDate, versionNumber)) {
        try {
          await store.projects.lock(id);
        } catch (e) {
          Toast.danger(`Project lock failed`);
          return;
        }
        Toast.success(`Project locked`);
      }
    } else {
      try {
        const { hasDeletedRecords } = await api.projects.unlockCheck(id);
        if (hasDeletedRecords) {
          if (!(await confirmProjectUnlock())) {
            return;
          }
        }
        await store.projects.unlock(id);
      } catch (e) {
        Toast.danger(`Project unlock failed`);
        return;
      }
      try {
        await store.requirements.list(id);
        store.requirements.commitCsv(id);
      } catch (e) {
        Toast.danger("Project unlocked, but requirements could not be refreshed.");
      }
      Toast.success(`Project unlocked`);
    }

    await initFn();

    try {
      await store.projects.detail(id);
    } catch (e) {
      Toast.danger("Project could not be updated.");
    }
  }, [initFn, project, store.projects, store.requirements]);

  const handleSave = useCallback(
    async (data: APITenderOutcomeBulkUpdateInput) => {
      try {
        await store.tenderOutcomes.bulkUpdate(data);
      } catch (e) {
        throw new Error("Clusters not updated");
      }
    },
    [store]
  );

  useSave(async () => {
    if (!editable) {
      throw new Error("Tender Outcome not editable");
    }
    await methods.handleSubmit(handleSave)();
  });

  if (project?.type !== "tender") {
    return <Container>Not a Tender Project - No Tender Outcome</Container>;
  }
  if (loading) {
    return <Loading full />;
  }

  return (
    <FormProvider {...methods}>
      <Container>
        <Form onSubmit={methods.handleSubmit(handleSave)}>
          <StyledDiv>
            <Header>Tender Outcome</Header>
            {hasEditPermissions && project && (
              <StyledButton onClick={handleClick}>
                {project.isLocked ? (
                  <>
                    <LockOpenOutlined /> Unlock
                  </>
                ) : (
                  <>
                    <LockOutlined /> Lock
                  </>
                )}{" "}
                Project
              </StyledButton>
            )}
          </StyledDiv>
          {loading ? (
            <Loading />
          ) : (
            <TenderOutcomeTable hasEditPermissions={hasEditPermissions} />
          )}
        </Form>
      </Container>
    </FormProvider>
  );
};

export default observer(TenderOutcome);
